import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, BadgeCheck, Landmark, ScrollText, Building2, FileCheck2, Copy, Check, ArrowRight, Info, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

/**
 * Premium Trust & Compliance strip.
 *
 * Each chip surfaces UKGM's statutory registrations with:
 *  - click-to-copy for numeric IDs (PAN, CSR-1, NGO Darpan)
 *  - an info popover that shows the issuing authority + verification URL
 *
 * Registration data is defined in a single editable ITEMS array — future
 * updates (e.g. adding a 12A / 80G unique ID once formally issued) just need
 * a value edit in this file.
 */

// Central editable registry — the single source of truth for compliance chips.
// value === null → non-copyable, shows sub as body text.
// value === 'Approved' → non-copyable status pill.
// value === '<real id>' → click-to-copy enabled.
// verify_url is optional; renders as an "Open registry" link inside the popover.
const ITEMS = [
  {
    key: 'trust',
    Icon: Landmark,
    label: 'Public Charitable Trust',
    sub: 'Bombay PT Act, 1950',
    value: null,
    authority: 'Charity Commissioner, Maharashtra',
    verify_url: null,
    note: 'Registered under the Bombay Public Trusts Act, 1950. Trust Deed governs all objectives and utilisation of funds.',
  },
  {
    key: 'pan',
    Icon: ScrollText,
    label: 'PAN',
    sub: 'Permanent Account Number',
    value: 'AABTU5738J',
    authority: 'Income Tax Department, Government of India',
    verify_url: null,
    note: 'Statutory Permanent Account Number of the Trust.',
  },
  {
    key: '12a',
    Icon: FileCheck2,
    label: '12A',
    sub: 'Income Tax Exemption',
    value: 'Approved',
    authority: 'Income Tax Department, Government of India',
    verify_url: null,
    note: 'Section 12A registration exempts the Trust from paying income tax on income applied to its charitable objects.',
  },
  {
    key: '80g',
    Icon: BadgeCheck,
    label: '80G',
    sub: 'Donor Tax Deduction',
    value: 'Approved',
    authority: 'Income Tax Department, Government of India',
    verify_url: null,
    note: 'Section 80G registration allows Indian donors to claim tax deduction on eligible contributions.',
  },
  {
    key: 'csr',
    Icon: Building2,
    label: 'CSR-1',
    sub: 'CSR Eligible Entity',
    value: 'CSR00106731',
    authority: 'Ministry of Corporate Affairs, Government of India',
    verify_url: 'https://www.mca.gov.in/mcafoportal/showCsrForms.do',
    note: 'CSR-1 filing certifies UKGM as an eligible implementing agency for Corporate Social Responsibility programmes.',
  },
  {
    key: 'darpan',
    Icon: ShieldCheck,
    label: 'NGO Darpan',
    sub: 'NGO Registry',
    value: 'MH/2025/0872011',
    authority: 'NITI Aayog, Government of India',
    verify_url: 'https://ngodarpan.gov.in/index.php/search/',
    note: 'Registered on NGO Darpan — the Government of India portal for NGO/VO information and grant eligibility.',
  },
];

const canCopy = (v) => v && v !== 'Approved';

export default function ComplianceStrip() {
  const [copied, setCopied] = useState('');

  const copy = async (v, k) => {
    if (!canCopy(v)) return;
    try {
      await navigator.clipboard?.writeText(v);
      setCopied(k);
      toast.success(`${v} copied`);
      setTimeout(() => setCopied(''), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <section
      className="relative overflow-x-hidden border-b border-ukgm-gold/30 max-w-[100vw]"
      style={{ background: 'linear-gradient(90deg, #0F1E4A 0%, #1E3A8A 50%, #172554 100%)' }}
      data-testid="compliance-strip"
      aria-label="Statutory registrations and compliance"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ukgm-gold/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 md:py-3.5 overflow-x-hidden">
        <div className="flex items-stretch gap-3 md:gap-4">
          {/* Chips row — horizontal scroll on all viewports if content overflows,
              with a subtle gold fade gradient on the right edge to hint at scrollability. */}
          <div className="relative flex-1 min-w-0">
            <div
              className="flex items-center gap-2 md:gap-2.5 overflow-x-auto snap-x snap-mandatory md:snap-none no-scrollbar"
              role="list"
              data-testid="compliance-chips-row"
            >
              {ITEMS.map((item) => (
                <ComplianceChip
                  key={item.key}
                  item={item}
                  copied={copied === item.key}
                  onCopy={() => copy(item.value, item.key)}
                />
              ))}
              {/* trailing spacer so last chip doesn't hide under the fade */}
              <div className="shrink-0 w-6" aria-hidden="true" />
            </div>
            {/* Right-edge fade indicating more content */}
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#172554] to-transparent"
              aria-hidden="true"
            />
          </div>

          <Link
            to="/transparency#compliance"
            className="hidden md:inline-flex items-center gap-2 shrink-0 self-center rounded-full bg-ukgm-gold text-ukgm-blue font-bold text-xs tracking-wide px-4 py-2 border border-ukgm-gold hover:bg-ukgm-goldDark transition-colors gold-glow"
            data-testid="compliance-view-btn"
          >
            View Compliance <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="mt-2.5 md:hidden">
          <Link
            to="/transparency#compliance"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ukgm-gold text-ukgm-blue font-bold text-xs px-4 py-2 border border-ukgm-gold hover:bg-ukgm-goldDark"
            data-testid="compliance-view-btn-mobile"
          >
            View Compliance <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-ukgm-gold/50 to-transparent" />
    </section>
  );
}

function ComplianceChip({ item, copied, onCopy }) {
  const { key, Icon, label, sub, value, authority, verify_url, note } = item;
  const isCopyable = canCopy(value);

  return (
    <div
      role="listitem"
      data-testid={`compliance-chip-${key}`}
      className={[
        'snap-start shrink-0 group inline-flex items-center gap-1',
        'rounded-full border transition-all duration-200',
        'pl-1 pr-1 md:pl-1.5 md:pr-1.5 py-1 md:py-1.5',
        'bg-white/[0.06] border-ukgm-gold/45 backdrop-blur-sm',
        'hover:bg-ukgm-gold/12 hover:border-ukgm-gold',
      ].join(' ')}
    >
      {/* Main body — click-to-copy when value is a real ID */}
      <button
        type="button"
        onClick={onCopy}
        disabled={!isCopyable}
        aria-label={value ? `${label} ${value}${isCopyable ? ' — click to copy' : ''}` : label}
        data-testid={`compliance-chip-copy-${key}`}
        className={[
          'inline-flex items-center gap-2 md:gap-2.5 rounded-full',
          'px-2 md:px-2.5 py-1',
          isCopyable
            ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ukgm-gold'
            : 'cursor-default',
        ].join(' ')}
      >
        <span className="w-5 h-5 md:w-6 md:h-6 shrink-0 rounded-full bg-ukgm-gold/15 border border-ukgm-gold/50 flex items-center justify-center">
          <Icon className="w-3 h-3 md:w-3.5 md:h-3.5 text-ukgm-gold" strokeWidth={2.25} />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[9px] md:text-[10px] tracking-[0.14em] uppercase font-semibold text-ukgm-gold/90 whitespace-nowrap">
            {label}
          </span>
          <span className="text-[11px] md:text-[12px] font-semibold text-white whitespace-nowrap tracking-tight">
            {value || sub}
          </span>
        </span>
        {isCopyable && (
          <span
            className={[
              'w-5 h-5 shrink-0 rounded-full flex items-center justify-center',
              'border transition-colors',
              copied
                ? 'bg-ukgm-gold text-ukgm-blue border-ukgm-gold'
                : 'text-ukgm-gold border-ukgm-gold/40 group-hover:border-ukgm-gold',
            ].join(' ')}
            aria-hidden="true"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-2.5 h-2.5" />}
          </span>
        )}
      </button>

      {/* Info popover — issuing authority + optional verify link */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`About ${label} — issuing authority and details`}
            data-testid={`compliance-chip-info-${key}`}
            className="w-5 h-5 md:w-6 md:h-6 shrink-0 rounded-full flex items-center justify-center text-ukgm-gold/80 hover:text-ukgm-gold hover:bg-ukgm-gold/15 border border-transparent hover:border-ukgm-gold/60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ukgm-gold"
          >
            <Info className="w-3 h-3 md:w-3.5 md:h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="center"
          sideOffset={10}
          className="w-[min(92vw,320px)] rounded-2xl border border-ukgm-gold/40 bg-white p-0 shadow-2xl"
          data-testid={`compliance-popover-${key}`}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center bg-ukgm-blue text-ukgm-gold border border-ukgm-gold/40">
                <Icon className="w-4 h-4" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-ukgm-gold">
                  {label}
                </div>
                <div className="mt-0.5 font-serif text-ukgm-blue font-semibold text-base leading-tight break-words">
                  {value || sub}
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-xl bg-ukgm-cream border border-ukgm-gold/25 p-3">
              <div className="text-[10px] tracking-widest uppercase text-ukgm-slate font-bold">Issuing Authority</div>
              <div className="mt-1 text-sm font-semibold text-ukgm-blue leading-snug">{authority}</div>
            </div>

            {note && (
              <p className="mt-3 text-xs text-ukgm-slate leading-relaxed">{note}</p>
            )}

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {isCopyable && (
                <button
                  type="button"
                  onClick={onCopy}
                  data-testid={`compliance-popover-copy-${key}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-ukgm-blue border border-ukgm-gold/40 rounded-full px-3 py-1.5 hover:bg-ukgm-goldLight"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy number'}
                </button>
              )}
              {verify_url && (
                <a
                  href={verify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`compliance-popover-verify-${key}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-ukgm-blue border border-ukgm-gold/40 rounded-full px-3 py-1.5 hover:bg-ukgm-goldLight"
                >
                  <ExternalLink className="w-3 h-3" /> Verify on registry
                </a>
              )}
              <Link
                to="/transparency#compliance"
                data-testid={`compliance-popover-view-${key}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-ukgm-blue bg-ukgm-gold rounded-full px-3 py-1.5 hover:bg-ukgm-goldDark ml-auto"
              >
                Full compliance <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
